/** XeAgents.ai - the only place a Cal.com calendar is mounted **/
import Cal, { getCalApi } from "@calcom/embed-react";
import { useEffect, useRef, useState } from "react";
import { CAL_BOOKING_URL, CAL_LINK, calNamespace } from "@shared/demo-booking";

export { CAL_LINK, CAL_BOOKING_URL, DEMO_EVENT, PILOT_CREDITS } from "@shared/demo-booking";

/**
 * Send the visitor to Cal.com's own booking page in a new tab.
 *
 * The inline embed is reserved for the ONE place that needs to know a booking
 * happened - the pilot modal, whose auto-submit hangs off the booking-success
 * event. Everywhere else only needs to get them to the calendar, and a link
 * does that without a second iframe (which Cal refuses under a shared
 * namespace) or ~300KB of embed script on first paint. The URL already carries
 * overlayCalendar=true, which only works on the full page anyway.
 */
export function openBookingPage(): void {
  window.open(CAL_BOOKING_URL, "_blank", "noopener,noreferrer");
}

export interface CalPrefill {
  name?: string;
  email?: string;
  notes?: string;
}

interface CalEmbedProps {
  prefill?: CalPrefill;
  /**
   * Fired at most once, when a booking completes. This advances the UI only -
   * the authoritative record is written by the signed Cal.com webhook.
   */
  onBookingSuccess?: () => void;
  className?: string;
}

/** Cal.com's embed iframe only ever posts from its own origin. */
function isCalOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname;
    return host === "cal.com" || host.endsWith(".cal.com");
  } catch {
    return false;
  }
}

export function CalEmbed({ prefill, onBookingSuccess, className }: CalEmbedProps) {
  // A namespace per mounted instance. Cal refuses a second inline embed under a
  // namespace it already has - it logs "Inline embed already exists" and draws
  // a blank box - which is what a landing-page calendar plus a modal calendar
  // produces, and what closing and reopening the modal produces on its own.
  const [namespace] = useState(() => calNamespace());

  // The callback identity changes on parent re-renders; a ref keeps the
  // listeners stable so they are not torn down and re-registered mid-booking.
  const successRef = useRef(onBookingSuccess);
  successRef.current = onBookingSuccess;

  // Three independent paths report the same booking (see below). The visitor
  // books once, so the first one through wins and the rest are dropped.
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
    let cancelled = false;

    const fireOnce = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      successRef.current?.();
    };

    // Path 3: read it straight off the iframe. The namespace listeners below
    // only work once Cal's real script has replaced the loader stub and drained
    // its queue; a booking completed before that is otherwise never reported.
    const onMessage = (event: MessageEvent) => {
      if (!isCalOrigin(event.origin)) return;
      const type = typeof event.data === "string" ? event.data : event.data?.type;
      if (typeof type === "string" && /bookingSuccessful/i.test(type)) fireOnce();
    };
    window.addEventListener("message", onMessage);

    (async function () {
      const cal = await getCalApi({ namespace });
      if (cancelled) return;
      cal("ui", { theme: "light", hideEventTypeDetails: false, layout: "month_view" });
      // Paths 1 and 2: Cal renamed this event and still emits the old name on
      // some versions, so both are registered rather than guessing which.
      cal("on", { action: "bookingSuccessful", callback: fireOnce });
      cal("on", { action: "bookingSuccessfulV2", callback: fireOnce });
    })();

    return () => {
      cancelled = true;
      window.removeEventListener("message", onMessage);
    };
  }, [namespace]);

  return (
    <Cal
      namespace={namespace}
      calLink={CAL_LINK}
      style={{ width: "100%", height: "100%", overflow: "scroll" }}
      className={className}
      config={{
        layout: "month_view",
        useSlotsViewOnSmallScreen: "true",
        theme: "light",
        // Lets the visitor overlay their own calendar on the slot picker.
        overlayCalendar: "true",
        ...(prefill?.name ? { name: prefill.name } : {}),
        ...(prefill?.email ? { email: prefill.email } : {}),
        ...(prefill?.notes ? { notes: prefill.notes } : {}),
      }}
    />
  );
}

export default CalEmbed;
